import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Zap, Handshake, FileCheck, CalendarCheck } from "lucide-react";
import { CONTACT } from "@/data/content";
import { SatisfactionBadge } from "@/components/landing/SatisfactionBadge";
import { AnimatedLines } from "@/components/landing/AnimatedLines";

const marble =
  "https://images.unsplash.com/photo-1515895309288-a3815ab7cf81?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ onReserve, onBook }) => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[85vh] flex items-center pt-12 md:pt-16 pb-24 overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${marble})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-marble-light/80 backdrop-blur-[2px]" />
      <AnimatedLines />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 w-full">
        <motion.div
          initial="hidden"
          animate="show"
          className="max-w-4xl"
        >
          <motion.div
            variants={fadeUp}
            custom={0}
            className="inline-flex items-center gap-2 bg-ink text-white text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-sm mb-7"
          >
            <Clock className="w-3.5 h-3.5" /> Zone {CONTACT.area}
          </motion.div>

          <motion.h1
            variants={fadeUp}
            custom={1}
            className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink"
          >
            Votre véhicule éclatant.
            <br />
            Votre habitat comme neuf.
            <br />
            <span className="text-royal">En un seul rendez-vous.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed"
          >
            Confiez tout à des pros — detailing auto, façades, terrasses, vitres, panneaux.
            Résultat visible immédiatement, <span className="font-semibold text-ink">sans y passer votre week-end.</span>
          </motion.p>

          <motion.div
            variants={fadeUp}
            custom={3}
            className="mt-9 flex flex-col sm:flex-row flex-wrap gap-4"
          >
            <button
              data-testid="hero-book-btn"
              onClick={onBook}
              className="cta group inline-flex items-center justify-center gap-2.5 bg-royal text-white font-extrabold text-lg px-10 py-5 rounded-sm hover:bg-royal-hover shadow-xl shadow-royal/30 w-full sm:w-auto"
            >
              <CalendarCheck className="w-6 h-6" />
              Réserver mon créneau
            </button>
            <button
              data-testid="hero-estimate-btn"
              onClick={() => onReserve("auto")}
              className="cta group inline-flex items-center justify-center gap-2 border-2 border-ink text-ink font-bold px-8 py-5 rounded-sm hover:bg-ink hover:text-white w-full sm:w-auto"
            >
              Obtenir mon devis immédiat
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

          <motion.div variants={fadeUp} custom={4} className="mt-5">
            <SatisfactionBadge />
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={4}
            className="mt-14 grid grid-cols-3 gap-5 max-w-2xl"
          >
            {[
              { icon: Zap, label: "Réactivité : réponse rapide" },
              { icon: Handshake, label: "Flexibilité maximale" },
              { icon: FileCheck, label: "Devis 24h urgences · 48h classiques" },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-2.5">
                <f.icon className="w-5 h-5 text-ink shrink-0" />
                <span className="text-xs sm:text-sm font-semibold text-ink leading-tight">
                  {f.label}
                </span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
